// Pure helpers for the Admin Strategy Requests tab (Custom Development admin view).
// Kept separate from the component so the logic is unit-testable in the repo's
// node-environment vitest setup (same convention as googleReviews.js).
import { genClientRequestId } from '../../lib/utils';

export const STRATEGY_REQUEST_PAGE_SIZE = 50;

// ─── Source type display ────────────────────────────────────────────────────────
// Mirrors app.custom_development.models.SourceType (backend enum) exactly.
const SOURCE_TYPE_LABELS = {
  RULES: 'Trading Rules',
  PINE_SCRIPT: 'Pine Script',
  PYTHON: 'Python',
  AFL: 'AFL (AmiBroker)',
  OTHER: 'Other',
};

export function sourceTypeLabel(value) {
  return SOURCE_TYPE_LABELS[value] ?? value ?? '—';
}

// ─── Message delivery status display ───────────────────────────────────────────
// Mirrors the CustomDevelopmentAdminMessageORM.delivery_status lifecycle exactly:
// PENDING while the send is in flight, SENT once delivered, FAILED if the mailer
// attempt errored (a new Idempotency-Key is required to retry a FAILED send).
const DELIVERY_STATUS_LABELS = {
  PENDING: 'Sending…',
  SENT: 'Sent',
  FAILED: 'Failed to send',
};

export function deliveryStatusLabel(status) {
  return DELIVERY_STATUS_LABELS[status] ?? status ?? '—';
}

// ─── Attachment limits ──────────────────────────────────────────────────────────
// Must stay identical to algosys-backend/app/custom_development/storage.py so
// the admin gets an immediate, accurate error instead of a round trip to the
// server. If the backend limits ever change, update both places together.
export const MAX_ATTACHMENT_FILES = 5;
export const MAX_ATTACHMENT_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENT_TOTAL_BYTES = 25 * 1024 * 1024;
export const ALLOWED_ATTACHMENT_EXTENSIONS = ['.txt', '.py', '.pine', '.afl', '.pdf', '.docx', '.png', '.jpg', '.jpeg'];

export function formatFileSize(bytes) {
  if (bytes == null) return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = n / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  const display = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${display} ${units[unitIndex]}`;
}

export function getFileExtension(filename) {
  const name = String(filename || '');
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex <= 0) return '';
  return name.slice(dotIndex).toLowerCase();
}

// Validates a candidate attachment selection (array-like of {name, size}, so it
// accepts real File objects as-is). Returns the first violation found, or null
// when the selection is within all backend limits.
export function validateAttachmentSelection(files) {
  const list = Array.from(files || []);
  if (list.length === 0) return null;
  if (list.length > MAX_ATTACHMENT_FILES) {
    return `A maximum of ${MAX_ATTACHMENT_FILES} files is allowed.`;
  }
  let totalBytes = 0;
  for (const file of list) {
    const ext = getFileExtension(file.name);
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
      return `"${file.name}" has an unsupported file type. Allowed: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_ATTACHMENT_FILE_BYTES) {
      return `"${file.name}" is larger than ${formatFileSize(MAX_ATTACHMENT_FILE_BYTES)}.`;
    }
    totalBytes += file.size;
  }
  if (totalBytes > MAX_ATTACHMENT_TOTAL_BYTES) {
    return `Total attachment size exceeds ${formatFileSize(MAX_ATTACHMENT_TOTAL_BYTES)}.`;
  }
  return null;
}

// ─── Idempotency-Key lifecycle ──────────────────────────────────────────────────
// Fresh Idempotency-Key for a message send attempt. Reuses lib/utils'
// genClientRequestId (same crypto.randomUUID()-with-fallback used by manual
// trading exit flows) so the whole app generates client-side idempotency
// tokens the same way.
export function newIdempotencyKey() {
  return genClientRequestId();
}

// Deterministic fingerprint of the composer's current contents, used to decide
// whether a retry is "the same request again" (reuse the key) or "an edited
// request" (mint a new key). Order-sensitive on file selection is fine since
// the admin can always re-add a removed file in the same order.
export function buildMessageSignature({ subject, message, files }) {
  const fileSignature = Array.from(files || []).map((f) => `${f.name}:${f.size}`).join('|');
  return JSON.stringify({ subject: subject ?? '', message: message ?? '', files: fileSignature });
}

// Pure decision: given the key/signature from the previous send attempt and the
// signature of what is about to be sent now, decide which Idempotency-Key to
// use. There is no previous attempt (or it was already cleared after a
// confirmed success) when previousKey is falsy — always mint a new key then.
// Otherwise, reuse the previous key only if nothing in the composer changed;
// any edit (subject, message, or attachments) mints a new key so the backend
// never treats an intentionally different message as a duplicate.
export function resolveIdempotencyKey({ previousKey, previousSignature, currentSignature }) {
  if (!previousKey || previousSignature == null) {
    return newIdempotencyKey();
  }
  return currentSignature === previousSignature ? previousKey : newIdempotencyKey();
}

// ─── Message history ────────────────────────────────────────────────────────────
// The admin list/messages endpoint returns newest-first (matches Website Leads'
// list conventions); the composer thread reads better oldest-first.
export function sortMessagesChronological(items) {
  return Array.from(items || []).slice().sort((a, b) => {
    const at = new Date(a?.created_at ?? 0).getTime();
    const bt = new Date(b?.created_at ?? 0).getTime();
    return at - bt;
  });
}

// ─── Downloads ───────────────────────────────────────────────────────────────────
// We already know the display filename from response metadata (original_filename
// on strategy/message-attachment records) before the download starts, so we
// never need to parse a Content-Disposition header off the shared axios
// response (which the global interceptor strips down to res.data anyway).
export function buildSignedNdaFilename(requestReference) {
  return `Signed-NDA-${requestReference}.pdf`;
}

// Triggers a browser "Save As" for an already-fetched Blob. doc/urlApi are
// injectable so this is unit-testable without jsdom (this repo has none — see
// TradingViewSetupGuide.test.jsx). Returns false (no-op) outside a browser.
export function triggerBrowserDownload(blob, filename, overrides = {}) {
  const doc = overrides.doc ?? (typeof document !== 'undefined' ? document : null);
  const urlApi = overrides.urlApi ?? (typeof URL !== 'undefined' ? URL : null);
  if (!doc || !urlApi || typeof urlApi.createObjectURL !== 'function') return false;

  const href = urlApi.createObjectURL(blob);
  const link = doc.createElement('a');
  link.href = href;
  link.download = filename;
  doc.body.appendChild(link);
  link.click();
  doc.body.removeChild(link);
  if (typeof urlApi.revokeObjectURL === 'function') urlApi.revokeObjectURL(href);
  return true;
}
